import React, {
  PropsWithChildren,
  useCallback,
  useLayoutEffect,
  useMemo,
} from "react";
import { Inset, LanguageNameSpaces, WorkspaceType } from "@shared/types";
import message from "antd/es/message";
import AntdApp from "antd/es/app";
import styled, { createGlobalStyle, css, useTheme } from "styled-components";
import ConfigProvider, { ThemeConfig } from "antd/es/config-provider";
import type { AppThemeType } from "@shared/common/Themes/AppTheme";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import { useSelectUser } from "@shared/utils/hooks/misc.ts";
import { selectWorkspace } from "@shared/slices/common.slice.ts";
import { useTelegramSafeAreaInsets } from "utils/hooks/useTelegramWebApp.ts";
import { useSelector } from "react-redux";
import { dropdownStyles } from "common/Dropdown/Dropdown.tsx";
import { popoverStyles } from "common/Popover/Popover.tsx";
import { selectStyle } from "common/Select/Select.tsx";
import { modalStyles } from "common/Modal/Modal.tsx";
import { datePickerStyles } from "common/DatePicker/DatePicker.tsx";
import { tooltipStyles } from "common/Tooltips/Tooltip.tsx";
import { rangePickerStyles } from "common/DatePicker/RangePicker.tsx";
import { collapsedMenuPopoverItems } from "common/Home/Sider/CollapsedMenu.ts";
import { drawerStyles } from "common/Drawers/Drawer.tsx";
import { textAreaStyles } from "common/Inputs/TextArea.tsx";
import { timePickerStyles } from "common/DatePicker/TimeRangePicker.tsx";
import { hexToRGB } from "@shared/utils/misc";
import { setMessageInstance } from "utils/misc";
import { IS_TELEGRAM_WEB_APP } from "features/telegram/constants";
import { getAntdLocale } from "utils/language.ts";
import Empty from "antd/es/empty";
import { Inbox, SearchX } from "lucide-react";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import EmptyIllustration from "common/Empty/EmptyIllustration.tsx";
import { ValueFlashStyle } from "common/NewLook/useValueFlash.ts";
import {
  PointerFocusStyle,
  useInputModality,
} from "common/NewLook/inputModality.ts";
import {
  NEW_LOOK_CARDS_VIEW_MAX_WIDTH,
  NEW_LOOK_PAGE_GUTTER,
  NEW_LOOK_TABLET_PAGE_GUTTER,
} from "common/NewLook/layout.ts";

export const NEW_LOOK_CLASS_NAME = "new-look";

// Option lists (selects, autocompletes...) get a one-line note instead of
// antd's grey inbox.
const OPTION_LIST_COMPONENTS = new Set([
  "Select",
  "AutoComplete",
  "Cascader",
  "TreeSelect",
  "Mentions",
]);

// Tables and lists show the new look's line illustration when empty.
const getNewLookEmptyRenderer =
  (noOptionsText: string) => (componentName?: string) => {
    if (componentName === "Table" || componentName === "List")
      return (
        <Empty
          image={<EmptyIllustration icon={Inbox} width={120} height={90} />}
          styles={{ image: { height: 90, marginBottom: 4 } }}
        />
      );
    if (componentName && OPTION_LIST_COMPONENTS.has(componentName))
      return (
        <OptionsEmpty>
          <SearchX size={16} strokeWidth={1.75} />
          {noOptionsText}
        </OptionsEmpty>
      );
    return undefined;
  };

// Menus and option lists: rounder than fields, tighter than cards.
const NEW_LOOK_POPUP_RADII = { borderRadiusLG: 12, borderRadiusSM: 8 };

// Fields darken on hover and show a soft blue ring only while focused.
const getNewLookFieldTokens = (theme: AppThemeType) => ({
  hoverBorderColor: theme.colors.mediumBorderGrey,
  activeBorderColor: theme.colors.primaryBlue,
  activeShadow: `0 0 0 3px ${hexToRGB(theme.colors.primaryBlue, 0.14)}`,
  activeOutlineColor: hexToRGB(theme.colors.primaryBlue, 0.14),
  errorActiveShadow: `0 0 0 3px ${hexToRGB(theme.colors.red, 0.14)}`,
  borderRadius: theme.radii.control,
  colorBorder: theme.colors.lightBorderGrey,
  // Placeholders stay legible on dark surfaces too.
  colorTextPlaceholder: theme.isDarkMode
    ? hexToRGB(theme.colors.secondaryText, 0.65)
    : theme.colors.lightestText,
  // Fields sit on the card surface in both themes (antd would paint white).
  colorBgContainer: theme.colors.cardBgColor,
});

const getNewLookComponents = (
  theme: AppThemeType
): ThemeConfig["components"] => ({
  Button: {
    primaryColor: theme.colors.buttonText,
    fontWeight: 560,
    // No focus ring on buttons (it also lingered after a modal handed focus
    // back to its trigger).
    lineWidthFocus: 0,
  },
  Input: getNewLookFieldTokens(theme),
  InputNumber: getNewLookFieldTokens(theme),
  DatePicker: getNewLookFieldTokens(theme),
  Mentions: getNewLookFieldTokens(theme),
  Form: {
    labelColor: theme.colors.primaryText,
    labelFontSize: 13,
    verticalLabelPadding: "0 0 6px",
  },
  Checkbox: { borderRadiusSM: 5 },
  // Off switches use a light track (not antd's heavy grey) and a soft handle.
  Switch: {
    colorTextQuaternary: theme.colors.checkboxBorderColor,
    colorTextTertiary: hexToRGB(theme.colors.secondaryText, 0.5),
    handleShadow: `0 1px 2px ${hexToRGB(theme.colors.titleColor, 0.24)}`,
  },
  Table: {
    headerColor: theme.colors.secondaryText,
    headerSortActiveBg: "transparent",
    headerSortHoverBg: "transparent",
  },
});

const DISABLE_ANT_MOTION_FOR_E2E =
  import.meta.env.VITE_E2E_DISABLE_ANT_MOTION === "true";
const BUTTON_CONFIG = { autoInsertSpace: false };

const AppThemeProvider = ({ children }: PropsWithChildren) => {
  const { safeAreaInset, contentSafeAreaInset } = useTelegramSafeAreaInsets();
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const user = useSelectUser();
  const theme = useTheme();
  const workspace = useSelector(selectWorkspace);
  const { t, i18n } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const noOptionsText = t("empty.no_options");
  const renderNewLookEmpty = useMemo(
    () => getNewLookEmptyRenderer(noOptionsText),
    [noOptionsText]
  );

  const colors = theme.colors;
  const isDarkMode = theme.isDarkMode;
  const isNewLook = theme.isNewLook;
  useInputModality(isNewLook);

  useLayoutEffect(() => {
    message.config({
      getContainer: () => document.getElementById("app")!,
    });
  }, []);

  const getPopupContainer = useCallback(() => {
    return document.getElementById("app")!;
  }, []);

  const antTheme: ThemeConfig = useMemo(() => {
    return {
      token: {
        ...(DISABLE_ANT_MOTION_FOR_E2E ? { motion: false } : {}),
        colorPrimary: colors.primary,
        fontFamily: theme.typography.fontFamily,
        colorBgElevated: colors.cardBgColor,
        colorBorderDisabled: colors.lightBorderGrey,
        colorText: colors.primaryText,
        colorTextDisabled: colors.disabledText,
        colorTextDescription: colors.secondaryGrey,
        // Tooltips: a raised surface in dark mode, so they stand out from
        // the cards and page behind them.
        colorBgSpotlight: isDarkMode ? colors.mediumBgColor : "#151515",
        ...(isNewLook
          ? {
              colorLink: colors.primaryBlue,
              borderRadius: 8,
              borderRadiusLG: theme.radii.card,
              borderRadiusSM: 6,
              colorBgSpotlight: isDarkMode ? colors.mediumBgColor : colors.ink,
              boxShadowSecondary: theme.shadows.hoverCard,
            }
          : {}),
      },
      components: {
        ...(isNewLook ? getNewLookComponents(theme) : {}),
        Steps: {
          colorTextDescription: colors.disabledText,
          colorTextLabel: colors.disabledText,
          colorPrimaryBg: colors.cardBgColor,
          colorPrimaryBgHover: colors.cardBgColor,
        },
        Segmented: {
          itemSelectedBg: colors.activeBgColor,
          trackBg: colors.tagBgColor,
          itemColor: isNewLook
            ? colors.primaryText
            : isDarkMode
            ? "#ffffff"
            : "#292B33",
          itemSelectedColor: isNewLook
            ? colors.primaryText
            : isDarkMode
            ? "#ffffff"
            : "#292B33",
          itemHoverColor: isNewLook
            ? colors.primaryText
            : isDarkMode
            ? "#ffffff"
            : "#292B33",
        },
        Menu: {
          itemBg: isNewLook ? "transparent" : colors.bgColor,
          itemHoverBg:
            isNewLook && !isBottomNavigator
              ? hexToRGB(colors.primaryBlue, 0.06)
              : "transparent",
          itemColor: colors.basic,
          itemHoverColor: isBottomNavigator ? colors.basic : colors.primaryBlue,
          itemBorderRadius: isNewLook ? theme.radii.control : 0,
          ...(isNewLook
            ? {
                activeBarWidth: 0,
                activeBarBorderWidth: 0,
                ...(!isBottomNavigator && { itemHeight: 36 }),
              }
            : {}),
          itemSelectedBg: colors.lightBlue,
          itemSelectedColor: colors.primaryBlue,
          groupTitleColor: colors.secondaryGrey,
          popupBg: colors.cardBgColor,
          itemMarginInline: 0,
          itemMarginBlock: 0,
          subMenuItemSelectedColor: colors.primaryBlue,
          iconSize: 16,
        },
        Select: {
          selectorBg: colors.cardBgColor,
          colorText: colors.primaryText,
          colorTextPlaceholder: colors.lightestGrey,
          colorTextDescription: colors.secondaryGrey,
          colorBorder: colors.lightBorderGrey,
          colorPrimaryHover: colors.primaryBlue,
          controlOutline: hexToRGB(colors.primaryBlue, 0.2),
          borderRadius: isNewLook ? theme.radii.control : 6,
          ...(isNewLook ? getNewLookFieldTokens(theme) : {}),
          optionSelectedBg: colors.titleCardBgColor,
          optionActiveBg: colors.tableHoverBgColor,
          optionSelectedColor: colors.primaryText,
          colorIcon: colors.secondaryGrey,
          colorIconHover: colors.secondaryGrey,
          clearBg: colors.cardBgColor,
          multipleItemBg: colors.lightBackground,
          multipleItemBorderColor: colors.lightBorderGrey,
          multiItemBorderColor: colors.lightBorderGrey,
          colorBgContainerDisabled: colors.cardBgColor,
          colorTextDisabled: colors.basic,
          ...(isNewLook ? NEW_LOOK_POPUP_RADII : {}),
        },
        Timeline: {
          tailColor: colors.lightestBorderGrey,
        },
        ...(isNewLook
          ? {
              Dropdown: {
                ...NEW_LOOK_POPUP_RADII,
                paddingBlock: 7,
                controlItemBgHover: colors.cardLightBgColor,
              },
              Tooltip: { borderRadius: 8 },
              Popover: { borderRadiusLG: 12 },
              Message: { borderRadiusLG: 12 },
            }
          : {}),
      },
    };
  }, [colors, isBottomNavigator, isDarkMode, isNewLook, theme]);

  return (
    <>
      {isNewLook && <ValueFlashStyle />}
      {isNewLook && <PointerFocusStyle />}
      {isBottomNavigator && (
        <IntercomStyles
          $hideLauncher={
            (!!user && !user?.is_onboarding) ||
            workspace === WorkspaceType.ADMIN
          }
          $isWebApp={IS_TELEGRAM_WEB_APP}
          $bottom={safeAreaInset.bottom + contentSafeAreaInset.bottom}
          $top={safeAreaInset.top + contentSafeAreaInset.top}
        />
      )}
      <ConfigProvider
        getPopupContainer={getPopupContainer}
        theme={antTheme}
        button={BUTTON_CONFIG}
        locale={getAntdLocale(i18n.language)}
        renderEmpty={isNewLook ? renderNewLookEmpty : undefined}
      >
        <AntdApp>
          <MessageBridge />
          <Wrapper
            id="app"
            className={isNewLook ? NEW_LOOK_CLASS_NAME : undefined}
            $safeAreaInset={safeAreaInset}
            $contentSafeAreaInset={contentSafeAreaInset}
          >
            {children}
          </Wrapper>
        </AntdApp>
      </ConfigProvider>
    </>
  );
};

const MessageBridge = () => {
  const { message: messageInstance } = AntdApp.useApp();
  React.useEffect(() => {
    setMessageInstance(messageInstance);
  }, [messageInstance]);
  return null;
};

export default AppThemeProvider;

const IntercomStyles = createGlobalStyle<{
  $hideLauncher?: boolean;
  $top?: number;
  $bottom?: number;
  $isWebApp?: boolean;
}>`
  ${(p) =>
    p.$isWebApp &&
    `
    .intercom-messenger-frame {
      background-color: black !important;
    }
    .intercom-messenger-frame iframe {
      box-sizing: border-box !important;
      padding-top: ${p.$top}px !important;
      padding-bottom: ${p.$bottom}px !important;
    }
  `};

  ${(p) =>
    p.$hideLauncher &&
    `
    .intercom-launcher {
      display: none;
    }
  `}

  ;
`;
const Wrapper = styled.div<{
  $safeAreaInset?: Inset;
  $contentSafeAreaInset?: Inset;
}>`
  background: ${(p) => p.theme.colors.bgColor};
  min-height: 100vh;
  min-width: 100vw;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      --new-look-field-border: ${p.theme.colors.lightBorderGrey};
      --new-look-subtle-bg: ${p.theme.colors.cardLightBgColor};
      --new-look-muted-text: ${p.theme.colors.mutedText};
      --new-look-secondary-text: ${p.theme.colors.secondaryText};
      --new-look-hairline: ${p.theme.colors.cardBorderColor};
      --new-look-canvas: ${p.theme.colors.canvasBgColor};
      --new-look-title: ${p.theme.colors.titleColor};
      --new-look-card-bg: ${p.theme.colors.cardBgColor};
      --new-look-page-gutter: ${NEW_LOOK_PAGE_GUTTER}px;
      @media (max-width: ${NEW_LOOK_CARDS_VIEW_MAX_WIDTH}) {
        --new-look-page-gutter: ${NEW_LOOK_TABLET_PAGE_GUTTER}px;
      }
    `};
  ${(p) => p.theme.scrollbarStyle};
  ${dropdownStyles};
  ${popoverStyles};
  ${selectStyle};
  ${modalStyles};
  ${datePickerStyles};
  ${tooltipStyles};
  ${rangePickerStyles};
  ${collapsedMenuPopoverItems};
  ${drawerStyles};
  ${textAreaStyles};
  ${timePickerStyles};

  .ant-drawer.ant-drawer-left {
    transition: opacity 0.2s;

    &:not(.ant-drawer-open) {
      opacity: 0;
    }
  }

  .ant-message-top {
    top: ${(p) =>
      IS_TELEGRAM_WEB_APP
        ? (p.$safeAreaInset?.top ?? 0) + (p.$contentSafeAreaInset?.top ?? 0)
        : 0}px !important;
  }

  * {
    font-family: ${(p) => p.theme.typography.fontFamily};
  }
`;

const OptionsEmpty = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 12px;
  color: ${(p) => p.theme.colors.secondaryText};
  font-size: 13px;
  line-height: 18px;
  svg {
    flex: none;
    opacity: 0.8;
  }
`;
